import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CustomerForm } from "@/components/customers/CustomerForm";
import { CustomerList } from "@/components/customers/CustomerList";
import { CustomerView } from "@/components/customers/CustomerView";
import { Header } from "@/components/Header";
import cloudflare from "@/integrations/cloudflare/client";

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

const Customers = () => {
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleAdd = () => {
    setSelectedCustomer(null);
    setIsFormOpen(true);
  };

  const handleEdit = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsFormOpen(true);
  };

  const handleView = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsViewOpen(true);
  };

  const handleFormSuccess = () => {
    setRefreshTrigger((prev) => prev + 1);
    setIsFormOpen(false);
  };

  const handleEditFromView = () => {
    setIsViewOpen(false);
    setIsFormOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedCustomer) return;

    if (
      !confirm(
        `Are you sure you want to delete ${selectedCustomer.first_name} ${selectedCustomer.last_name}?`
      )
    ) {
      return;
    }

    try {
      const response = await fetch(`${cloudflare.apiUrl}/api/v1/customers/${selectedCustomer.id}`, {
        method: "DELETE",
      });

      if (response.status === 409) {
        const error = await response.json();
        toast.error(error.error, {
          description: error.details,
        });
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to delete customer");
      }

      toast.success("Customer deleted successfully");
      setIsViewOpen(false);
      setSelectedCustomer(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      toast.error("Failed to delete customer", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        onNewProject={() => navigate("/new-project")}
        onViewProjects={() => navigate("/projects")}
      />

      <main className="container mx-auto px-4 py-6">
        <CustomerList
          onAdd={handleAdd}
          onEdit={handleEdit}
          onView={handleView}
          refreshTrigger={refreshTrigger}
        />

        <CustomerForm
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSuccess={handleFormSuccess}
          customer={selectedCustomer}
        />

        <CustomerView
          isOpen={isViewOpen}
          onClose={() => setIsViewOpen(false)}
          onEdit={handleEditFromView}
          onDelete={handleDelete}
          customer={selectedCustomer}
        />
      </main>
    </div>
  );
};

export default Customers;
